import { DateTime } from 'luxon'
import { AvailabilityDataRecord, AvailabilityEntry, DateKey } from '../../types'

interface CalendarUtilsOptions {
  timezone: string
  minStay: number
  maxStay: number
  maxNumDays: number
  advanceNoticeHours?: number | null
}

interface ValidationResult {
  status: boolean
  message?: string
  reason?: 'advance_notice'
  date?: string
}

interface SelectableRange {
  minDate: string
  maxDate: string
}

/**
 * Calendar utilities for availability checking, date validation,
 * and min/max stay enforcement. TypeScript port of HomeLocalCalendarUtils.
 */
export class CalendarUtils {
  private dailyPrice: AvailabilityDataRecord = {} as AvailabilityDataRecord
  private timezone: string
  private spokenDates = new Map<string, string>()
  private minStay: number
  private maxStay: number
  private maxNumDays: number
  private minDate: DateTime
  private maxDate: DateTime
  private maxIterations: number
  private advanceNoticeHours: number
  private noticeBoundary: DateTime
  /**
   * Epoch ms of the next moment `_refreshClock` could have anything to do.
   * The clock check sits in front of the cache lookup in `isUnavailable` — the
   * innermost call of every availability walk — so it has to cost a number
   * compare, not a Luxon round trip. Measured on luxon 3.7.2/node 22: the
   * unguarded body was 8.97 µs/call, or 27.50 µs once an advance-notice window
   * made it recompute the boundary too. At ~390 cells x 3 calls that was
   * 10.5-32.2 ms of main-thread time PER RENDER, and the grid re-renders on
   * every hover.
   */
  private _nextClockEventMs: number
  /**
   * Bumped whenever `_refreshClock` flushes. Accessors capture it before a
   * walk and refuse to write a result computed across a flush — otherwise a
   * value accumulated under the OLD bounds lands in the FRESH cache and
   * outlives the very invalidation meant to discard it.
   */
  private _cacheGen = 0

  // Caches
  private _cache: {
    isUnavailable: Record<string, boolean>
    canCheckin: Record<string, ValidationResult>
    canCheckout: Record<string, ValidationResult>
    getAvailableCheckoutDays: Record<string, string[]>
    selectableRanges: Record<string, SelectableRange>
    minstay: Record<string, number>
    maxstay: Record<string, number>
    trapped: Record<string, boolean>
  }

  constructor(options: CalendarUtilsOptions) {
    this.timezone = options.timezone || 'UTC'
    this.minStay = options.minStay || 1
    this.maxStay = options.maxStay || 365
    this.maxNumDays = options.maxNumDays || 365
    this.minDate = this.getDateToday()
    this.maxDate = this.minDate.plus({ days: this.maxNumDays })
    this.maxIterations = this.maxNumDays + 60
    // Advance-notice window (#1071, toolkit advance-notice-blocking parity).
    // The earliest bookable check-in is the calendar date of (now + hours) in
    // the PROPERTY timezone, so the boundary is identical for every visitor
    // regardless of their browser timezone (the toolkit's F16 lesson: local
    // clock math is off by up to a day for remote guests).
    const noticeHours = Number(options.advanceNoticeHours ?? 0)
    this.advanceNoticeHours = Number.isFinite(noticeHours) && noticeHours > 0 ? noticeHours : 0
    this.noticeBoundary = this._computeNoticeBoundary()
    this._nextClockEventMs = this._computeNextClockEventMs()

    this._cache = {
      isUnavailable: {},
      canCheckin: {},
      canCheckout: {},
      getAvailableCheckoutDays: {},
      selectableRanges: {},
      minstay: {},
      maxstay: {},
      trapped: {},
    }
  }

  setPriceData(prices: AvailabilityDataRecord): void {
    this.dailyPrice = prices
    this._cache = {
      isUnavailable: {},
      canCheckin: {},
      canCheckout: {},
      getAvailableCheckoutDays: {},
      selectableRanges: {},
      minstay: {},
      maxstay: {},
      trapped: {},
    }
  }

  hasPriceData(): boolean {
    return Object.keys(this.dailyPrice).length > 0
  }

  getFirstWeekday(year: number, month: number): number {
    const dt = DateTime.fromObject({ year, month, day: 1 }, { zone: this.timezone })
    return dt.weekday // 1=Monday, 7=Sunday (Luxon convention)
  }

  getMonthDayCount(year: number, month: number): number {
    const dt = DateTime.fromObject({ year, month, day: 1 }, { zone: this.timezone })
    return dt.daysInMonth ?? 30
  }

  dateYmd(date: DateTime | Date): DateKey {
    if (date instanceof Date) {
      const dt = DateTime.fromJSDate(date, { zone: this.timezone })
      return dt.toFormat('yyyy-MM-dd') as DateKey
    }
    return date.toFormat('yyyy-MM-dd') as DateKey
  }

  getDateZero(dateStr?: string): DateTime {
    if (!dateStr) {
      return DateTime.now().setZone(this.timezone).startOf('day')
    }
    return DateTime.fromISO(dateStr, { zone: this.timezone }).startOf('day')
  }

  /**
   * Spoken date for a day cell: "Friday, October 23, 2026". Formatted by Luxon
   * in the property's zone (getDateZero returns a DateTime, never a JS Date), so
   * the day never shifts for a visitor whose browser zone differs. Latin digits
   * and the Gregorian calendar keep the cell's visible day number inside the
   * name in every locale — WCAG 2.5.3 label-in-name (#1865).
   */
  spokenDate(dateStr: string, locale?: string): string {
    // Cached: the grid re-renders ~390 cells on every hover while a check-out
    // is picked, and Intl formatting each one costs 15-35 ms a pass.
    const key = `${dateStr}|${locale ?? ''}`
    let spoken = this.spokenDates.get(key)
    if (spoken === undefined) {
      spoken = this.formatSpokenDate(dateStr, locale)
      this.spokenDates.set(key, spoken)
    }
    return spoken
  }

  private formatSpokenDate(dateStr: string, locale?: string): string {
    const dt = this.getDateZero(dateStr)
    if (!dt.isValid) return dateStr
    const opts = { numberingSystem: 'latn', outputCalendar: 'gregory' }
    try {
      return dt.reconfigure({ ...opts, locale: locale || 'en-US' }).toLocaleString(DateTime.DATE_HUGE)
    } catch {
      return dt.reconfigure({ ...opts, locale: 'en-US' }).toLocaleString(DateTime.DATE_HUGE)
    }
  }

  prevDay(dateStr: string): DateKey {
    const dt = this.getDateZero(dateStr)
    return this.dateYmd(dt.minus({ days: 1 }))
  }

  nextDay(dateStr: string): DateKey {
    const dt = this.getDateZero(dateStr)
    return this.dateYmd(dt.plus({ days: 1 }))
  }

  getDateToday(): DateTime {
    return DateTime.now().setZone(this.timezone).startOf('day')
  }

  dateOptions(date: string | DateTime | Date): AvailabilityEntry | undefined {
    let key: DateKey
    if (typeof date === 'string') {
      key = date as DateKey
    } else if (date instanceof Date) {
      key = this.dateYmd(date)
    } else {
      key = this.dateYmd(date)
    }
    return this.dailyPrice[key]
  }

  getMinstay(day: string): number {
    if (this._cache.minstay[day] !== undefined) {
      return this._cache.minstay[day]
    }
    const gen = this._cacheGen
    const result = this._calculateMinstay(day)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.minstay[day] = result
    return result
  }

  getMaxstay(day: string): number {
    if (this._cache.maxstay[day] !== undefined) {
      return this._cache.maxstay[day]
    }
    const gen = this._cacheGen
    const result = this._calculateMaxstay(day)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.maxstay[day] = result
    return result
  }

  calcNights(date1: string | DateTime, date2: string | DateTime): number {
    const d1 = typeof date1 === 'string' ? this.getDateZero(date1) : date1
    const d2 = typeof date2 === 'string' ? this.getDateZero(date2) : date2
    return Math.ceil(Math.abs(d1.diff(d2, ['days']).days))
  }

  /**
   * Before today or past the rendered window — a day with no rule to explain,
   * as distinct from one the availability data blocks. `isUnavailable` conflates
   * the two, which made hover tips fire on every leading greyed cell.
   */
  isOutsideWindow(day: string): boolean {
    this._refreshClock()
    const dayDate = this.getDateZero(day)
    return dayDate < this.minDate || dayDate > this.maxDate
  }

  isUnavailable(day: string): boolean {
    // Rendering paths enter here first — keep the property-day clock live so
    // a grid painted across midnight re-derives past/window bounds (codex r2).
    this._refreshClock()
    if (this._cache.isUnavailable[day] !== undefined) {
      return this._cache.isUnavailable[day]
    }
    const gen = this._cacheGen
    const result = this._calculateUnavailability(day)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.isUnavailable[day] = result
    return result
  }

  isToday(dateStr: string): boolean {
    return this.getDateToday().toISODate() === dateStr
  }

  private _computeNoticeBoundary(): DateTime {
    return this.advanceNoticeHours > 0
      ? DateTime.now().setZone(this.timezone).plus({ hours: this.advanceNoticeHours }).startOf('day')
      : this.minDate
  }

  /**
   * Keep the clock-derived state live on a long-lived instance (codex r1/r2).
   * Two rollovers matter: the property-timezone DAY (minDate/maxDate — after
   * midnight, yesterday must read as past, not advance-notice, and the far
   * boundary moves), and the advance-notice cutoff (a sub-24h window flips
   * "today" to blocked the moment now + hours crosses midnight). A day roll
   * flushes everything date-derived; a boundary-only roll flushes the
   * canCheckin-derived caches so a stale "bookable" can't survive the cutoff.
   */
  /**
   * When the derived clock state can next change: the next property-timezone
   * midnight, and — with an advance-notice window — the moment `now + hours`
   * crosses midnight and moves the boundary a day forward.
   */
  private _computeNextClockEventMs(): number {
    const nextMidnight = this.minDate.plus({ days: 1 }).toMillis()
    if (this.advanceNoticeHours <= 0) return nextMidnight
    const nextBoundaryShift =
      this.noticeBoundary.plus({ days: 1 }).toMillis() - this.advanceNoticeHours * 3600_000
    return Math.min(nextMidnight, nextBoundaryShift)
  }

  private _refreshClock(): void {
    // Hot path: one number compare. Everything below only runs on the tick a
    // rollover actually happens.
    if (Date.now() < this._nextClockEventMs) return
    this._refreshClockSlow()
  }

  private _refreshClockSlow(): void {
    const today = this.getDateToday()
    if (today.toISODate() !== this.minDate.toISODate()) {
      this.minDate = today
      this.maxDate = today.plus({ days: this.maxNumDays })
      this.noticeBoundary = this._computeNoticeBoundary()
      this._cache = {
        isUnavailable: {},
        canCheckin: {},
        canCheckout: {},
        getAvailableCheckoutDays: {},
        selectableRanges: {},
        minstay: {},
        maxstay: {},
        trapped: {},
      }
      this._cacheGen++
      this._nextClockEventMs = this._computeNextClockEventMs()
      return
    }
    if (this.advanceNoticeHours <= 0) {
      // Same property-day and no window: nothing moves until the next midnight.
      this._nextClockEventMs = this._computeNextClockEventMs()
      return
    }
    const fresh = this._computeNoticeBoundary()
    if (fresh.toISODate() === this.noticeBoundary.toISODate()) {
      this._nextClockEventMs = this._computeNextClockEventMs()
      return
    }
    this.noticeBoundary = fresh
    this._cache.canCheckin = {}
    this._cache.canCheckout = {}
    this._cache.getAvailableCheckoutDays = {}
    this._cache.selectableRanges = {}
    this._cache.trapped = {}
    this._cacheGen++
    this._nextClockEventMs = this._computeNextClockEventMs()
  }

  /**
   * Whether a date falls inside the advance-notice window: not yet past,
   * but too soon to book under the property's advance_notice_hours rule.
   * Past dates stay the calendar's existing "unavailable" case.
   */
  isNoticeBlocked(day: string): boolean {
    if (this.advanceNoticeHours <= 0) return false
    this._refreshClock()
    const dayDate = this.getDateZero(day)
    return dayDate >= this.minDate && dayDate < this.noticeBoundary
  }

  /**
   * Advance-notice window in whole days, measured off the boundary the grid is
   * actually painting (0 when no window). `Math.ceil(hours / 24)` overstates it
   * for any non-multiple of 24: 30 hours evaluated at 01:00 gives a boundary of
   * tomorrow, i.e. exactly ONE blocked day, while the naive round-up says two —
   * and that number is the one the tooltip shows the guest.
   */
  getAdvanceNoticeDays(): number {
    if (this.advanceNoticeHours <= 0) return 0
    this._refreshClock()
    return Math.max(1, Math.round(this.noticeBoundary.diff(this.minDate, 'days').days))
  }

  /** Earliest check-in date the advance-notice window allows, or null when no window. */
  getNoticeBoundaryDate(): string | null {
    if (this.advanceNoticeHours <= 0) return null
    this._refreshClock()
    return this.dateYmd(this.noticeBoundary)
  }

  isTrapped(day: string): boolean {
    if (this._cache.trapped[day] !== undefined) {
      return this._cache.trapped[day]
    }
    const gen = this._cacheGen
    const result = this._calculateIsTrapped(day)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.trapped[day] = result
    return result
  }

  canCheckin(dateStr: string): ValidationResult {
    // The cache must not outlive a notice-boundary rollover (codex r1);
    // refresh first so a stale "bookable" can never be served from it.
    this._refreshClock()
    if (this._cache.canCheckin[dateStr] !== undefined) {
      return this._cache.canCheckin[dateStr]
    }
    const gen = this._cacheGen
    const result = this._calculateCanCheckin(dateStr)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.canCheckin[dateStr] = result
    return result
  }

  canCheckinout(checkin: string, checkout: string): ValidationResult {
    const checkinResult = this.canCheckin(checkin)
    if (!checkinResult.status) {
      return checkinResult
    }

    // A checkout outside the calendar window (or with no data) is never
    // valid, even when its flags look bookable — without this, a click on a
    // rendered-but-disabled tail day past maxNumDays was accepted (codex r1).
    if (this.isUnavailable(checkout)) {
      return { status: false, message: 'Checkout unavailable' }
    }

    if (!this._canCheckoutFlag(checkout)) {
      return { status: false, message: 'Checkout unavailable' }
    }

    const checkinDate = this.getDateZero(checkin)
    const checkoutDate = this.getDateZero(checkout)

    if (checkoutDate <= checkinDate) {
      return { status: false, message: 'Checkout must be after check-in' }
    }

    const nights = this.calcNights(checkin, checkout)
    const minStay = this.getMinstay(checkin)
    const maxStay = this.getMaxstay(checkin)

    if (nights < minStay) {
      return { status: false, message: `Minimum stay is ${minStay} nights` }
    }

    if (nights > maxStay) {
      return { status: false, message: `Maximum stay is ${maxStay} nights` }
    }

    // Check continuous availability
    let current = this.nextDay(checkin)
    let iterations = 0
    while (current !== checkout && iterations < 366) {
      if (!this._canStay(current)) {
        return { status: false, message: 'Unavailable dates in range' }
      }
      current = this.nextDay(current)
      iterations++
    }

    return { status: true }
  }

  canCheckout(checkout: string): ValidationResult {
    if (this._cache.canCheckout[checkout] !== undefined) {
      return this._cache.canCheckout[checkout]
    }
    const gen = this._cacheGen
    const result = this._calculateCanCheckout(checkout)
    // Only commit if no flush happened mid-walk (see _cacheGen).
    if (this._cacheGen === gen) this._cache.canCheckout[checkout] = result
    return result
  }

  getFirstCheckinDay(): string | null {
    let current = this.dateYmd(this.getDateToday())
    let iterations = 0
    while (iterations < this.maxIterations) {
      if (this.isUnavailable(current)) {
        current = this.nextDay(current)
        iterations++
        continue
      }
      const result = this.canCheckin(current)
      if (result.status) {
        return current
      }
      current = this.nextDay(current)
      iterations++
    }
    return null
  }

  getSelectableRange(checkinStr: string): SelectableRange | null {
    if (this._cache.selectableRanges[checkinStr] !== undefined) {
      return this._cache.selectableRanges[checkinStr]
    }
    const gen = this._cacheGen
    const result = this._calculateSelectableRange(checkinStr)
    if (result && this._cacheGen === gen) {
      this._cache.selectableRanges[checkinStr] = result
    }
    return result
  }

  getAvailableCheckoutDays(checkinStr: string): string[] {
    if (this._cache.getAvailableCheckoutDays[checkinStr] !== undefined) {
      return this._cache.getAvailableCheckoutDays[checkinStr]
    }
    const gen = this._cacheGen
    const result = this._calculateAvailableCheckoutDays(checkinStr)
    if (this._cacheGen === gen) this._cache.getAvailableCheckoutDays[checkinStr] = result
    return result
  }

  // --- Raw flag checks ---

  _canCheckinFlag(day: string): boolean {
    const opts = this.dateOptions(day)
    return opts?.checkin === 1
  }

  _canCheckoutFlag(day: string): boolean {
    const opts = this.dateOptions(day)
    return opts?.checkout === 1
  }

  _checkinBooked(day: string): boolean {
    const opts = this.dateOptions(day)
    return opts?.checkin === 2
  }

  _checkoutBooked(day: string): boolean {
    const opts = this.dateOptions(day)
    return opts?.checkout === 2
  }

  _canStay(day: string): boolean {
    const opts = this.dateOptions(day)
    return opts?.available === 1
  }

  // --- Private calculations ---

  private _calculateUnavailability(day: string): boolean {
    const dayDate = this.getDateZero(day)
    if (dayDate < this.minDate) return true
    if (dayDate > this.maxDate) return true
    const opts = this.dateOptions(day)
    return !opts
  }

  private _calculateMinstay(day: string): number {
    const opts = this.dateOptions(day)
    if (opts && opts.min_stay !== undefined) {
      const val = Math.floor(opts.min_stay)
      if (val > 0) return Math.min(val, 100)
      return 1
    }
    return this.minStay
  }

  private _calculateMaxstay(day: string): number {
    const opts = this.dateOptions(day)
    if (opts && opts.max_stay !== undefined) {
      const val = Math.floor(opts.max_stay)
      if (val > 0) return val
    }
    return this.maxStay || 365
  }

  private _calculateCanCheckin(dateStr: string): ValidationResult {
    if (this.isUnavailable(dateStr)) {
      return { status: false, message: 'Unavailable' }
    }

    // Advance-notice window outranks the flag checks (toolkit precedence:
    // its patch answers before the host pipeline) — the window is the
    // actionable explanation when both apply.
    if (this.isNoticeBlocked(dateStr)) {
      return {
        status: false,
        reason: 'advance_notice',
        // Deliberately NOT user-facing copy: `reason` is the discriminator the
        // UI translates through error_advance_notice_error. A literal here is a
        // second, untranslatable copy of that string, and getTooltipMessage's
        // fall-through renders `message` verbatim — so it leaked raw English
        // onto non-English feeds.
        message: `advance_notice:${this.getAdvanceNoticeDays()}`,
      }
    }

    if (!this._canCheckinFlag(dateStr)) {
      return { status: false, message: 'Check-in unavailable' }
    }

    const dayDate = this.getDateZero(dateStr)
    if (dayDate < this.minDate) {
      return { status: false, message: 'Date is in the past' }
    }

    const minStay = this.getMinstay(dateStr)
    // Check continuous availability for min stay
    for (let i = 1; i < minStay; i++) {
      const nextDate = this.dateYmd(dayDate.plus({ days: i }))
      if (!this._canStay(nextDate)) {
        return { status: false, message: `Minimum stay of ${minStay} nights required` }
      }
    }

    // Check that at least one checkout day exists
    const checkoutDays = this.getAvailableCheckoutDays(dateStr)
    if (checkoutDays.length === 0) {
      return { status: false, message: 'No available checkout dates' }
    }

    return { status: true }
  }

  private _calculateCanCheckout(checkout: string): ValidationResult {
    if (this.isUnavailable(checkout)) {
      return { status: false, message: 'Unavailable' }
    }

    if (!this._canCheckoutFlag(checkout)) {
      return { status: false, message: 'Checkout unavailable' }
    }

    // Walk backward to find if there's a valid check-in
    let current = this.prevDay(checkout)
    let iterations = 0
    while (iterations < this.maxIterations) {
      if (this.isUnavailable(current)) break
      const checkinResult = this.canCheckin(current)
      if (checkinResult.status) {
        const checkoutDays = this.getAvailableCheckoutDays(current)
        if (checkoutDays.includes(checkout)) {
          return { status: true }
        }
      }
      current = this.prevDay(current)
      iterations++
    }

    return { status: false, message: 'No valid check-in for this checkout' }
  }

  private _calculateIsTrapped(day: string): boolean {
    if (this.isUnavailable(day)) return false

    const canStay = this._canStay(day)
    const canCI = this._canCheckinFlag(day)
    const canCO = this._canCheckoutFlag(day)

    if (!canStay && !canCI && !canCO) return false

    // If it's a valid check-in or checkout, not trapped
    if (this.canCheckin(day).status) return false
    if (this.canCheckout(day).status) return false

    // Scan backward for nearest valid check-in
    let current = this.prevDay(day)
    let iterations = 0
    while (iterations < this.maxIterations) {
      if (this.isUnavailable(current)) return true

      const checkinResult = this.canCheckin(current)
      if (checkinResult.status) {
        const range = this.getSelectableRange(current)
        if (range) {
          const dayDate = this.getDateZero(day)
          const maxDate = this.getDateZero(range.maxDate)
          if (dayDate <= maxDate) return false
        }
        return true
      }
      current = this.prevDay(current)
      iterations++
    }

    return true
  }

  private _calculateAvailableCheckoutDays(checkinStr: string): string[] {
    const checkinDate = this.getDateZero(checkinStr)
    const minStay = this.getMinstay(checkinStr)
    const maxStay = this.getMaxstay(checkinStr)
    const result: string[] = []

    let current = this.dateYmd(checkinDate.plus({ days: minStay }))
    let nights = minStay
    let iterations = 0

    while (nights <= maxStay && iterations < this.maxIterations) {
      // isUnavailable also caps the walk at the calendar window (maxNumDays):
      // availability data can extend past it, and enumerating those tail days
      // let the nearest-valid-stay action select a date the grid renders as
      // disabled (codex r1). Subsumes the old bare no-data check.
      // isUnavailable already covers the no-data case (_calculateUnavailability
      // returns true when dateOptions is undefined) as well as the window cap.
      if (this.isUnavailable(current)) break

      if (this._checkoutBooked(current)) break

      if (this._canCheckoutFlag(current)) {
        result.push(current)
      } else if (!this._canStay(current) && !this._canCheckoutFlag(current)) {
        break
      }

      current = this.nextDay(current)
      nights++
      iterations++
    }

    return result
  }

  private _calculateSelectableRange(checkinStr: string): SelectableRange | null {
    const checkoutDays = this.getAvailableCheckoutDays(checkinStr)
    if (checkoutDays.length === 0) return null

    return {
      // First VALID checkout (checkoutDays already starts at checkin+minStay),
      // so dates inside the min-stay window gray out like the WP plugin (#603).
      minDate: checkoutDays[0],
      maxDate: checkoutDays[checkoutDays.length - 1],
    }
  }
}
